/**
 * 要素からスタイルを取得
 * ※ getComputedStyleに対応しないブラウザへの対策
 *
 * @param {*} element スタイルを取得する要素
 * @param {*} styleName 取得するプロパティ名
 * @param {*} pseudoElt 一致させたい疑似要素
 * @return {string} 見つかったプロパティの値を返す
 */
export const getStyleValue = (element:any, styleName:any = "", pseudoElt:any = "") => {
    var style;

    var toCamelcase = (str:string, upper:boolean = false) => {
        if (!str) return str;

        var strs = str.split(/[-_ ]+/),
            i = 1, len = strs.length;

        if (len <= 1) return str;

        if (upper) {
            i = 0;
            str = "";
        } else {
            str = strs[0].toLowerCase();
        }

        for (; i < len; i++) {
            str += strs[i].toLowerCase().replace(/^[a-z]/, (value) => value.toUpperCase());
        }

        return str;
    };

    if (document.defaultView) {
        style = document.defaultView.getComputedStyle(element, pseudoElt);
    } else {
        style = element.currentStyle;
        styleName = toCamelcase(styleName);
    }
    return (styleName) ? style[styleName] : style;
};

/**
 * 要素からフォントサイズを取得
 *
 * @param {*} element 取得する元の要素
 * @return {number} フォントサイズを単位なしで返す
 */
const getFontSize = (element:any) => {
    let fontSize = getStyleValue(element, "font-size");

    if (!fontSize.match(/px$/)) {
        var tmpElem = document.createElement("div");
        tmpElem.style.width = "1em";
        element.appendChild(tmpElem);
        fontSize = tmpElem.offsetWidth;
        element.removeChild(tmpElem);
    }

    return parseInt(fontSize);
};

/**
 * 要素内にあるテキストの行数を取得
 *
 * @param {*} element
 * @return {number}
 */
export const getNumberLines = (element:any) => {
    var count      = 0;
    var calcResult = 0;

    var lineHeightVal = getStyleValue(element, "line-height");
    var heightVal     = element.clientHeight;

    var boxSizing = getStyleValue(element, "box-sizing");
    if (boxSizing === "border-box" || boxSizing === "padding-box") {
        heightVal -= parseInt(getStyleValue(element, "padding-top"));
        heightVal -= parseInt(getStyleValue(element, "padding-bottom"));
    }

    if (isNaN(parseInt(lineHeightVal))) {
        let fontSize = getFontSize(element);
        while (calcResult < heightVal) {
            calcResult += fontSize;
            if (calcResult <= heightVal) count++;
        }
    } else {
        count = Math.floor(heightVal / parseInt(lineHeightVal));
    }

    return count;
};

/**
 * チャットエリアの高さ揃え
 */
export const adjustArea = () => {
    let chatElem             = <HTMLElement>document.querySelector(".chatbot-area");
    let historyElem          = <HTMLElement>chatElem.querySelector(".wc-message-groups");
    let spaceVal             = 20;
    let areaHeightVal        = chatElem.offsetHeight;
    let historyAreaHeightVal = historyElem.offsetHeight;
    let windowHeightVal      = window.innerHeight;

    historyAreaHeightVal += windowHeightVal - areaHeightVal - spaceVal - spaceVal;

    if (areaHeightVal > windowHeightVal - spaceVal - spaceVal) return;

    historyElem.style.height = historyAreaHeightVal + "px";
};

/**
 * Mediaクエリによるスマートフォン判定
 * @return {object} メディアクエリのオブジェクトを返す
 */
export const isSmartPhoneMediaQuery = (() => {
    if (!window.matchMedia) return { matches : false, addListener : function() {} };
    return window.matchMedia("screen and (max-device-width:767px)");
})();

/**
 * Element.classList.containsのIE9対応
 * https://www.tjcafferkey.me/ie9-classname-containsclass-alternative/
 */
export const containsClass = (elem:any, yourClass:any) => {
    if (!elem || typeof yourClass !== "string") {
        return false;
    } else if (elem.className && elem.className.trim().split(/\s+/gi).indexOf(yourClass) > -1) {
        return true;
    } else {
        return false;
    }
}

/**
 * Element.classList.addのIE9対応
 * @param {Element} element 付与する要素
 * @param {string} classNameValue 付与するクラス名
 * @return {Element} 付与する要素を返す
 */
export const addClass = (element:any, classNameValue:any) => {
    if (!element || typeof element.className === 'undefined' || typeof classNameValue !== 'string') return element;

    var inArray = (searchValue:any, arrayData:any) => {
        for (var key in arrayData) {
            if (arrayData[key] === searchValue) return key;
        }
        return -1;
    };

    if (element.classList) {
        element.classList.add(classNameValue);
    } else {
        let classNames = element.className.replace(/^\s+|\s+$/g, '').split(' ');

        if (classNames.toString() === '') classNames = [];
        if (inArray(classNameValue, classNames) > -1) return element;

        classNames.push(classNameValue);

        element.className = classNames.join(' ');
    }

    return element;
};

/**
 * Element.classList.removeのIE9対応
 * @param {Element} element 削除する要素
 * @param {string} classNameValue 削除するクラス名
 * @return {Element} 削除する要素を返す
 */
export const removeClass = function(element:any, classNameValue:any) {
    if (!element || !element.className || typeof classNameValue !== 'string') return element;

    var inArray = (searchValue:any, arrayData:any) => {
        for (var key in arrayData) {
            if (arrayData[key] === searchValue) return key;
        }
        return -1;
    };

    if (element.classList) {
        element.classList.remove(classNameValue);
    } else {
        let classNames   = element.className.replace(/^\s+|\s+$/g, '').split(' '),
            hasClassName = false;

        if (inArray(classNameValue, classNames) === -1) return element;
        if (classNames.toString() === '') classNames = [];

        for (let i = 0, len = classNames.length; i < len; i++) {
            if (classNames[i] !== classNameValue) continue;

            classNames.splice(i, 1);
            hasClassName = true;
            break;
        }

        if (hasClassName) element.className = classNames.join(' ');
    }

    return element;
};

/**
 * href属性にパラメータを追加する関数
 * @param {string} hrefVal 追加先のhref属性値
 * @param {string} addParamVal 追加するパラメータ（フォーマット：key=value）
 * @return {string} パラメータが追加されたhref属性値を返す
 */
export const hrefAddParamVal = (hrefVal:string, addParamVal:string) => {
    let href = hrefVal;
    let hashVal = "";
    let addHrefVal;

    // ハッシュタグの対応
    if (hrefVal.indexOf("#") >= 0) {
        href = hrefVal.split("#")[0];
        hashVal = hrefVal.split("#")[1];
    }

    // パラメータの対応
    if (href.indexOf("?") >= 0) {
        addHrefVal = href.split("?")[0] + "?" + addParamVal + ((href.split("?")[1]) ? "&" + href.split("?")[1] : "");
    } else {
        addHrefVal = href + "?" + addParamVal;
    }

    if (hashVal) {
        addHrefVal = addHrefVal + "#" + hashVal;
    }

    return addHrefVal;
};

/**
 * ハッシュをマージする
 * @param {*} args マージするハッシュ
 * @return {*} マージされたハッシュを返す
 */
export const hashMerge = (... args:any[]) => {
    if (args.length === 0) return false;

    let i:number, len:number, key:any, result:any = {};

    for (i = 0, len = args.length; i < len; i++) {
        for (key in args[i]) {
            result[key] = args[i][key];
        }
    }

    return result;
};

/**
 * ハッシュのプロパティの数を数える
 * @param {Object} hash 数えるハッシュ
 * @return {number} プロパティ数を返す
 */
export const hashLength = (hash:any) => {
    let result = 0;

    if (!hash || typeof hash !== "object") return 0;
    for (let key in hash) result++;

    return result;
};

/**
 * UserAgentによるデバイス判定
 * @return {string} スマートフォンの場合は"sp"、タブレットの場合は"tab"、それ以外の場合は"other"を返す
 */
export const getDevice = (() => {
    const ua = navigator.userAgent;
    if (
        ua.indexOf("iPhone") > 0 || ua.indexOf("iPod") > 0 ||
        ua.indexOf("Android") > 0 && ua.indexOf("Mobile") > 0
    ) {
        return "sp";
    } else if (ua.indexOf("iPad") > 0 || ua.indexOf("Android") > 0) {
        return "tab";
    } else {
        return "other";
    }
})();

/**
 * ベジェ曲線座標の取得
 * @param {number} t 現在の時間
 * @param {number} x1 開始X座標
 * @param {number} y1 開始Y座標
 * @param {number} x2 制御1X座標
 * @param {number} y2 制御1Y座標
 * @param {number} x3 制御2X座標
 * @param {number} y3 制御2Y座標
 * @param {number} x4 終了X座標
 * @param {number} y4 終了Y座標
 * @return {{x:number,y:number}} 現在の座標を返す
 */
export const getPointBezierCurve = (t:number, x1:number, y1:number, x2:number, y2:number, x3:number, y3:number, x4:number, y4:number) => {
    const tp = 1 - t;
    const x = t * t * t * x4 + 3 * t * t * tp * x3 + 3 * t * tp * tp * x2 + tp * tp * tp * x1;
    const y = t * t * t * y4 + 3 * t * t * tp * y3 + 3 * t * tp * tp * y2 + tp * tp * tp * y1;

    return { x : x, y : y };
};

/**
 * Easing Functions
 * https://gist.github.com/gre/1650294
 */
export const easings = {
    // no easing, no acceleration
    linear: (t:any) => t,
    // accelerating from zero velocity
    easeInQuad: (t:any) => t*t,
    // decelerating to zero velocity
    easeOutQuad: (t:any) => t*(2-t),
    // acceleration until halfway, then deceleration
    easeInOutQuad: (t:any) => t<.5 ? 2*t*t : -1+(4-2*t)*t,
    // accelerating from zero velocity
    easeInCubic: (t:any) => t*t*t,
    // decelerating to zero velocity
    easeOutCubic: (t:any) => (--t)*t*t+1,
    // acceleration until halfway, then deceleration
    easeInOutCubic: (t:any) => t<.5 ? 4*t*t*t : (t-1)*(2*t-2)*(2*t-2)+1,
    // accelerating from zero velocity
    easeInQuart: (t:any) => t*t*t*t,
    // decelerating to zero velocity
    easeOutQuart: (t:any) => 1-(--t)*t*t*t,
    // acceleration until halfway, then deceleration
    easeInOutQuart: (t:any) => t<.5 ? 8*t*t*t*t : 1-8*(--t)*t*t*t,
    // accelerating from zero velocity
    easeInQuint: (t:any) => t*t*t*t*t,
    // decelerating to zero velocity
    easeOutQuint: (t:any) => 1+(--t)*t*t*t*t,
    // acceleration until halfway, then deceleration
    easeInOutQuint: (t:any) => t<.5 ? 16*t*t*t*t*t : 1+16*(--t)*t*t*t*t
};

declare global {
    interface String {
        toEnEN(): string;
        toEmKana(): string;
    }
}

/**
 * 全角英数字から半角英数字へ変換
 *
 * @return {string} 変換された文字列
 */
String.prototype.toEnEN = function() {
    var searchText  = [];
    var replaceText = [];
    var resultText  = "";
    var oneText     = "";
    var index       = 0;

    searchText = [
        "０", "１", "２", "３", "４", "５", "６", "７", "８", "９",
        "ａ", "ｂ", "ｃ", "ｄ", "ｅ", "ｆ", "ｇ", "ｈ", "ｉ", "ｊ", "ｋ", "ｌ", "ｍ", "ｎ", "ｏ", "ｐ", "ｑ", "ｒ", "ｓ", "ｔ", "ｕ", "ｖ", "ｗ", "ｘ", "ｙ", "ｚ",
        "Ａ", "Ｂ", "Ｃ", "Ｄ", "Ｅ", "Ｆ", "Ｇ", "Ｈ", "Ｉ", "Ｊ", "Ｋ", "Ｌ", "Ｍ", "Ｎ", "Ｏ", "Ｐ", "Ｑ", "Ｒ", "Ｓ", "Ｔ", "Ｕ", "Ｖ", "Ｗ", "Ｘ", "Ｙ", "Ｚ"
    ];

    replaceText = [
        "0", "1", "2", "3", "4", "5", "6", "7", "8", "9",
        "a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m", "n", "o", "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z",
        "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z"
    ];

    for (var i = 0, len = this.length; i < len; i++) {
        oneText = this.charAt(i);
        index   = searchText.indexOf(oneText);
        if(index != -1) {
            resultText += replaceText[index];
        } else {
            resultText += oneText;
        }
    }

    return resultText;
};

/**
 * 半角カタカナから全角カタカナへ変換
 *
 * @return {string} 変換された文字列
 */
String.prototype.toEmKana = function() {
    var searchText  = "";
    var replaceText = "";
    var resultText  = "";
    var c;
    var n;
    var cnext;
    var nnext;

    searchText  = "ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜｦﾝｧｨｩｪｫｬｭｮｯ､｡ｰ｢｣ﾞﾟ";
    replaceText = "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲンァィゥェォャュョッ、。ー「」" +
                    "　　ヴ　　ガギグゲゴザジズゼゾダヂヅデド　　　　　バビブベボ　　　　　　　　　　　　　　　　　　　　　　　　　　　　　　" +
                    "　　　　　　　　　　　　　　　　　　　　　　　　　パピプペポ　　　　　　　　　　　　　　　　　　　　　　　　　　　　　　";

    for (var i = 0, len = this.length; i < len; ++i) {
        c     = this.charAt(i);
        cnext = this.charAt(i + 1);
        n     = searchText.indexOf(c, 0);
        nnext = searchText.indexOf(cnext, 0);

        if(n >= 0) {
            if (nnext == 60) {
                c = replaceText.charAt(n + 60);
                i++;
            } else if (nnext == 61) {
                c = replaceText.charAt(n + 120);
                i++;
            } else {
                c = replaceText.charAt(n);
            }
        }
        if(!String(n).match(/^(6[01])$/)) {
            resultText += c;
        }
    }
    return resultText;
};



// WEBPACK FOOTER //
// ./src/utilites.ts